import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  Toolbar,
  Button,
  Select,
  FormControl,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { format, isPast } from 'date-fns';
import { vi } from 'date-fns/locale';
import taskService from '../../services/taskService';

const PRIORITY_CONFIG = {
  low: { color: '#94a3b8', label: 'Thấp' },
  medium: { color: '#3b82f6', label: 'Trung bình' },
  high: { color: '#f59e0b', label: 'Cao' },
  urgent: { color: '#ef4444', label: 'Khẩn cấp' },
};

const STATUS_CONFIG = {
  todo: { color: '#64748b', label: 'Chờ xử lý' },
  in_progress: { color: '#3b82f6', label: 'Đang làm' },
  review: { color: '#f59e0b', label: 'Chờ review' },
  done: { color: '#10b981', label: 'Hoàn thành' },
};

const CATEGORY_CONFIG = {
  marketing: { color: '#8b5cf6', label: 'Marketing' },
  social: { color: '#06b6d4', label: 'Social' },
  content: { color: '#10b981', label: 'Content' },
  production: { color: '#f59e0b', label: 'Sản xuất' },
  report: { color: '#6366f1', label: 'Báo cáo' },
};

const TaskListView = ({ tasks, loading, onTaskClick, onTaskUpdated, onTaskDeleted }) => {
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Flatten tasks from kanban structure
  const allTasks = tasks ? [
    ...(tasks.todo || []),
    ...(tasks.in_progress || []),
    ...(tasks.review || []),
    ...(tasks.done || []),
  ] : [];

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedTasks = [...allTasks].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === 'deadline') {
      aValue = aValue ? new Date(aValue) : new Date('9999-12-31');
      bValue = bValue ? new Date(bValue) : new Date('9999-12-31');
    }

    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(allTasks.map(t => t._id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter(s => s !== id);
    }

    setSelected(newSelected);
  };

  const handleMenuOpen = (event, task) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTask(null);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const result = await taskService.updateTaskStatus(taskId, newStatus);
      if (result.success) {
        onTaskUpdated?.(result.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteTask = async () => {
    if (selectedTask && window.confirm('Bạn có chắc muốn xóa task này?')) {
      try {
        await taskService.deleteTask(selectedTask._id);
        onTaskDeleted?.(selectedTask._id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
    handleMenuClose();
  };

  const handleBulkDelete = async () => {
    if (selected.length > 0 && window.confirm(`Xóa ${selected.length} task đã chọn?`)) {
      try {
        await Promise.all(selected.map(id => taskService.deleteTask(id)));
        selected.forEach(id => onTaskDeleted?.(id));
        setSelected([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden',
      }}
    >
      {/* Bulk Actions Toolbar */}
      {selected.length > 0 && (
        <Toolbar
          sx={{
            bgcolor: '#eff6ff',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Typography sx={{ flex: 1 }}>
            Đã chọn {selected.length} task
          </Typography>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
          >
            Xóa
          </Button>
        </Toolbar>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < allTasks.length}
                  checked={allTasks.length > 0 && selected.length === allTasks.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Tiêu đề
                </TableSortLabel>
              </TableCell>
              <TableCell>Phân loại</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'priority'}
                  direction={orderBy === 'priority' ? order : 'asc'}
                  onClick={() => handleSort('priority')}
                >
                  Độ ưu tiên
                </TableSortLabel>
              </TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Người phụ trách</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'deadline'}
                  direction={orderBy === 'deadline' ? order : 'asc'}
                  onClick={() => handleSort('deadline')}
                >
                  Deadline
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTasks.map((task) => {
              const isSelected = selected.includes(task._id);
              const isOverdue = task.deadline && isPast(new Date(task.deadline)) && task.status !== 'done';
              const priorityConfig = PRIORITY_CONFIG[task.priority];
              const statusConfig = STATUS_CONFIG[task.status];
              const categoryConfig = CATEGORY_CONFIG[task.category];

              return (
                <TableRow
                  key={task._id}
                  hover
                  selected={isSelected}
                  onClick={() => onTaskClick?.(task)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isOverdue ? '#fef2f2' : 'inherit',
                    '&:hover': {
                      bgcolor: isOverdue ? '#fee2e2' : '#f8fafc',
                    },
                  }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectOne(task._id)}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {task.description}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={categoryConfig?.label}
                      size="small"
                      sx={{
                        bgcolor: categoryConfig?.color + '20',
                        color: categoryConfig?.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={priorityConfig?.label}
                      size="small"
                      sx={{
                        bgcolor: priorityConfig?.color + '20',
                        color: priorityConfig?.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={task.status}
                        onChange={(e) => handleStatusChange(task._id, e.target.value)}
                        sx={{
                          fontSize: '0.75rem',
                          '& .MuiSelect-select': {
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          },
                        }}
                      >
                        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                          <MenuItem key={key} value={key} sx={{ fontSize: '0.75rem' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: config.color,
                                mr: 1,
                              }}
                            />
                            {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    {task.assignee ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#667eea' }}>
                          {task.assignee.fullName?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontSize="0.8rem">
                          {task.assignee.fullName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        Chưa giao
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {task.deadline ? (
                      <Tooltip title={format(new Date(task.deadline), 'dd/MM/yyyy HH:mm', { locale: vi })}>
                        <Chip
                          label={format(new Date(task.deadline), 'dd/MM/yyyy', { locale: vi })}
                          size="small"
                          sx={{
                            bgcolor: isOverdue ? '#fee2e2' : '#f1f5f9',
                            color: isOverdue ? '#ef4444' : '#64748b',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)}>
                      <MoreIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {sortedTasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">Chưa có task nào</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { onTaskClick?.(selectedTask); handleMenuClose(); }}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          Xem chi tiết
        </MenuItem>
        <MenuItem onClick={() => { onTaskClick?.(selectedTask); handleMenuClose(); }}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Chỉnh sửa
        </MenuItem>
        {selectedTask?.status !== 'done' && (
          <MenuItem onClick={() => { handleStatusChange(selectedTask._id, 'done'); handleMenuClose(); }}>
            <DoneIcon sx={{ mr: 1, fontSize: 20, color: '#10b981' }} />
            Hoàn thành
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Xóa
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default TaskListView;
